import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classnames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { List, FocusZone } from '@bingads-webui-react/fabric-control';
import { ImageExtensionItemPreview } from './image-extension-item-preview';
import { ImageExtensionItemSeeMoreLess } from './image-extension-item-see-more-less';
import { getImageExtensionListStyles, ITEM_MARGIN } from '../styles/image-extensions-list-style';
import { IMAGE_WIDTH_PX, IMAGE_HEIGHT_PX } from './utils/parser';

export const ImageExtensionsList = ({
  i18n,
  items,
  className,
  onDelete,
  onSelect,
  onEditImage,
  enableEdit,
}) => {
  const classes = useDefaultStyles(getImageExtensionListStyles, {
    cellWidth: IMAGE_WIDTH_PX,
    cellHeight: IMAGE_HEIGHT_PX,
  });
  const [imageContainerWidth, setImageContainerWidth] = useState(0);
  const imageContainerNode = useRef(null);
  const [showAll, setShowAll] = useState(false);
  const itemsSize = _.size(items);
  const initialItemCount = Math.floor((imageContainerWidth + ITEM_MARGIN)
    / (IMAGE_WIDTH_PX + ITEM_MARGIN)) * 2;
  const hasSeeMoreLess = itemsSize > initialItemCount;

  useEffect(() => {
    const handleResize = () => {
      if (imageContainerNode.current) {
        setImageContainerWidth(imageContainerNode.current.offsetWidth);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const measuredRef = useCallback((node) => {
    if (node) {
      imageContainerNode.current = node;
      setImageContainerWidth(imageContainerNode.current.offsetWidth);
    }
  }, []);

  const CELL_TYPE = {
    MORE: 'more',
    LESS: 'less',
    EXTENSION: 'extension',
  };

  const getCellType = (index) => {
    const isSeeMoreItem = !showAll && index === initialItemCount - 1 && hasSeeMoreLess;
    const isSeeLessItem = showAll && index === itemsSize && hasSeeMoreLess;

    if (isSeeMoreItem) {
      return CELL_TYPE.MORE;
    } else if (isSeeLessItem) {
      return CELL_TYPE.LESS;
    }
    return CELL_TYPE.EXTENSION;
  };

  const onRenderCell = (item, index) => {
    const cellType = getCellType(index);
    switch (cellType) {
      case CELL_TYPE.MORE:
        return (<ImageExtensionItemSeeMoreLess
          i18n={i18n}
          onSelectShowAll={setShowAll}
          seeMoreItemsCount={(itemsSize - (initialItemCount - 1))}
        />);
      case CELL_TYPE.LESS:
        return (<ImageExtensionItemSeeMoreLess
          i18n={i18n}
          onSelectShowAll={setShowAll}
          seeLess={showAll}
        />);
      default:
        return (<ImageExtensionItemPreview
          onDelete={onDelete}
          onSelect={onSelect}
          onEditImage={onEditImage}
          enableEdit={enableEdit}
          item={item}
          index={index}
          i18n={i18n}
        />);
    }
  };

  return (
    !_.isEmpty(items) &&
    <div className={classnames(className, classes.listContainer)} ref={measuredRef}>
      <FocusZone>
        <List
          className={className}
          items={_.compact(_.union(items, [hasSeeMoreLess]))}
          onRenderCell={onRenderCell}
          renderCount={showAll ? itemsSize + 1 : initialItemCount}
          getItemCountForPage={() => initialItemCount}
          role="listbox"
          aria-label={i18n.getString(_TL_('Image extensions'))}
        />
      </FocusZone>
    </div>
  );
};

ImageExtensionsList.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  onSelect: PropTypes.func,
  onDelete: PropTypes.func,
  onEditImage: PropTypes.func.isRequired,
  enableEdit: PropTypes.bool,
  className: PropTypes.string,
};

ImageExtensionsList.defaultProps = {
  items: [],
  onSelect: null,
  onDelete: null,
  className: '',
  enableEdit: false,
};
